<script lang="ts" setup>
import type { PresentationPage } from '~/models/Presentation/BasePage'
import type { Component } from 'vue'
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue'
import type { BaseItem } from '~/models/Content/BaseItem'
import type { Presentation } from '~/models/Presentation/Presentation'
import type { TextAndImage } from '~/models/Presentation/Pages/GridPage'
import { ContentType } from '~/models/Content/ContentType'
import { useSearch } from '~/composables/useSearch'
import useSearchHelper from '~/composables/useSearchHelper'
import useArrayUtils from '~/utils/arrayUtils'
import useContentMapper from '~/composables/useContentMapper'
import { useContentHelper } from '~/composables/useContentHelper'
import ThreeStepPageComponent from '~/components/PresentationPages/ThreeStepPage.vue'
import GridPageComponent from '~/components/PresentationPages/GridPage.vue'
import SideBySidePageComponent from '~/components/PresentationPages/SideBySidePage.vue'
import PresentationPageNavigation from '~/components/PresentationPages/Components/PresentationPageNavigation.vue'
import { useRouter } from 'vue-router'
import ImageAndMaybeTextComponent from '~/components/PresentationPages/ImageAndMaybeText.vue'
import OnlyTextPageComponent from '~/components/PresentationPages/OnlyTextPage.vue'
import RelatedContentPage from '~/components/PresentationPages/RelatedContentPage.vue'
import useEnvironment from '~/composables/useEnvironment'
import CloseButton from '~/components/utils/CloseButton.vue'
import AudioPage from '../PresentationPages/AudioPage.vue'
import VideoPage from '~/components/PresentationPages/VideoPage.vue'

const props = defineProps<{
  pageContentIds: number[]
  pageNumber: number
  presentation: Presentation
}>()

const pageComponents: Record<string, Component> = {
  [ContentType.ThreeStepPage]: ThreeStepPageComponent,
  [ContentType.GridPage]: GridPageComponent,
  [ContentType.SideBySidePage]: SideBySidePageComponent,
  [ContentType.ImageAndMaybeText]: ImageAndMaybeTextComponent,
  [ContentType.OnlyTextPage]: OnlyTextPageComponent,
  [ContentType.RelatedContentPage]: RelatedContentPage,
  [ContentType.AudioPage]: AudioPage,
  [ContentType.VideoPage]: VideoPage,
}

const emit = defineEmits<{ 'update:pageNumber': [pageNumber: number]; 'replace:pageNumber': [pageNumber: number] }>()

const { truthy } = useArrayUtils()
const { searchPath, emptyQuery, getContentIdCriterion } = useSearchHelper()
const { isGridPage  } = useContentHelper()
const { mapContents } = useContentMapper()

/**
 * Helper function to abstract away the search logic. This is slightly nicer than having separate
 * fetchResults per type you want to fetch.
 *
 * @param contentIds ContentIds to fetch
 */
const fetchContent = async <T extends BaseItem>(contentIds: number[]): Promise<T[]> => {
  if (!contentIds.length) return []
  const { results, fetchResults } = useSearch<T[]>(searchPath, {
    transformData: (response) => mapContents(response.View.Result)
  })
  await fetchResults(emptyQuery, getContentIdCriterion(contentIds), 100)
  return contentIds.map((contentId) => (results.value?.find((result) => result.contentId === contentId))).filter(truthy<T>)
}

const animationDirection = ref('')
const pages = ref<PresentationPage[]>([])
const router = useRouter()
const { isDev } = useEnvironment()

onMounted(async () => {
  try {
    pages.value = (await fetchContent<PresentationPage>(props.pageContentIds))
      .flatMap((page) => Array(page.subPages).fill(page).map((page, index) => ({ ...page, step: index + 1 })))
      .filter((page) => Object.keys(pageComponents).includes(page.contentTypeIdentifier)) // only include pages that has page components

    pages.value.forEach(async (page) => {
      if (isGridPage(page) && !Array.isArray(page.textAndImages)) {
        page.textAndImages = await fetchContent<TextAndImage>(page.textAndImages.destinationContentIds)
      }
    })
  } catch (error) {
    if (isDev()) throw error
    console.error('Error fetching content', error)
    await router.replace({
      name: 'notfound',
      query: { sti: router.currentRoute.value.fullPath }
    })
  }

  if (props.pageNumber > pages.value.length) emit('replace:pageNumber', pages.value.length)
  if (props.pageNumber < 1) emit('replace:pageNumber', 1)
})

const goToPage = (pageNumber: number, direction: string) => {
  if (!canGoToNextPage.value && pageNumber > totalPages.value) return
  if (!canGoToPreviousPage.value && pageNumber < 1) return
  if (direction === 'left') animationDirection.value = 'left'
  if (direction === 'right') animationDirection.value = 'right'

  emit('update:pageNumber', pageNumber)
}

const keyboardNavigation = (event: KeyboardEvent) => {
  if (event.altKey || event.ctrlKey || event.shiftKey || event.metaKey) return
  if (event.key === 'ArrowRight') goToPage(props.pageNumber + 1, 'right')
  if (event.key === 'ArrowLeft') goToPage(props.pageNumber - 1, 'left')
}

onBeforeMount(() => window.addEventListener('keydown', keyboardNavigation))
onBeforeUnmount(() => window.removeEventListener('keydown', keyboardNavigation))

const currentPageComponent = computed(() => pageComponents[currentPage.value.contentTypeIdentifier] || 'component')
const totalPages = computed(() => pages.value.length)
const currentPage = computed(() => pages.value[props.pageNumber - 1])
const canGoToNextPage = computed(() => props.pageNumber < totalPages.value)
const canGoToPreviousPage = computed(() => props.pageNumber > 1)
const translateX = computed(() => animationDirection.value === 'left' ? '-translate-x-full' : 'translate-x-full')

</script>
<template>
  <div
    v-if="currentPage"
    class="h-screen w-screen overflow-hidden font-inter transition-colors duration-1000"
    :style="{
      background: currentPage.colorPair.background,
      '--media-color': currentPage.colorPair.background,
      '--media-bg-color': currentPage.colorPair.text,
    }"
  >
    <CloseButton
      :item="presentation"
      :style="{
        background: currentPage.colorPair.background,
        color: currentPage.colorPair.text,
        border: `1.5px solid ${currentPage.colorPair.text}`
      }"
    />
    <Transition
      enter-active-class="duration-[900ms] ease-out delay-200"
      :enter-from-class="translateX"
      enter-to-class="translate-x-0"
      leave-active-class="duration-200 ease-out"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
      appear
    >
      <component
        :is="currentPageComponent"
        :key="currentPage.contentId"
        :page="currentPage"
        :current-page-number="pageNumber"
        :total-pages="totalPages"
        :animation-direction="animationDirection"
      />
    </Transition>
    <PresentationPageNavigation
      :page-number="pageNumber"
      :pages="pages || []"
      :presentation="presentation"
      @next-page="goToPage(pageNumber + 1, 'right')"
      @prev-page="goToPage(pageNumber - 1, 'left')"
    />
  </div>
</template>
